import { useState } from 'react'

import { UpsellingAlert as F1UpsellingAlert, type IconType } from '@factorialco/f0-react'
import { Upsell } from '@factorialco/f0-react/icons/app'

import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { ProductModal } from 'modules/upselling_kit/components/ProductModal'
import { UpsellGuard } from 'modules/upselling_kit/components/UpsellGuard'
import { UpsellingKitAlert } from 'modules/upselling_kit/types'
import { handleUpsellingVisibility } from 'modules/upselling_kit/utils/handlers'

const DEFAULT_ICON: IconType = Upsell

const UpsellAlertContent = ({
  title,
  description,
  icon,
  label,
  moduleRequested,
  requestSource,
  requestReason,
  featureRequested,
}: UpsellingKitAlert) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    handleUpsellingVisibility({
      moduleRequested,
      requestSource,
      component: 'upselling_alert',
    })
  }, [moduleRequested, requestSource])

  const handleRequest = async () => {
    track(trackingEvents.upsellingKit.clickProductUpsellComponent, {
      source: requestSource,
      module: moduleRequested,
      component: 'upselling_alert',
    })
    setIsModalOpen(true)
  }

  const alertIcon: IconType = icon ?? DEFAULT_ICON

  return (
    <>
      <F1UpsellingAlert
        icon={alertIcon}
        title={title}
        description={description}
        action={{
          label: label ?? i18n.t('upselling_kit.button.learn_more'),
          onRequest: handleRequest,
          errorMessage: {
            title: i18n.t('upselling_kit.upselling_button.error_message.title'),
            description: i18n.t('upselling_kit.upselling_button.error_message.description'),
          },
          successMessage: {
            title: i18n.t('upselling_kit.upselling_button.success_message.title_2'),
            description: i18n.t('upselling_kit.upselling_button.success_message.description'),
          },
          loadingState: {
            label: i18n.t('upselling_kit.upselling_button.loading_state.label'),
          },
          nextSteps: {
            title: '',
            items: [],
          },
          closeLabel: i18n.t('upselling_kit.upselling_button.close_label'),
        }}
      />
      {isModalOpen && (
        <ProductModal
          moduleRequested={moduleRequested}
          requestSource={requestSource}
          requestReason={requestReason}
          featureRequested={featureRequested}
          pageSource={requestSource}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          openFromUpsellButton={false}
          fallbackComponent={null}
          upsellRequestComponentType="upselling_alert"
        />
      )}
    </>
  )
}

const UpsellAlert = ({
  title,
  description,
  icon,
  label,
  fallbackComponent,
  loadingComponent,
  ...upsellProps
}: UpsellingKitAlert) => {
  return (
    <UpsellGuard
      {...upsellProps}
      fallbackComponent={fallbackComponent ?? null}
      loadingComponent={loadingComponent}
    >
      <UpsellAlertContent
        {...upsellProps}
        title={title}
        description={description}
        icon={icon}
        label={label}
      />
    </UpsellGuard>
  )
}

export { UpsellAlert }
